import React, { useRef, createContext, useContext } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { MachineStatusOverlay, StatusLight, PayoffIndicators, L75ModeIndicator } from './MachineStatus3D';

// Context to hide all 3D labels when a panel is open
const HideLabelsCtx = createContext(false);

// ─── COLORS ────────────────────────────────────────────────
const M = {
  frame: '#3A3A3A',
  body: '#5A6A7A',
  barrel: '#6A6A6A',
  motor: '#4A5060',
  hopper: '#B0B0B0',
  safety: '#E8B830',
  water: '#4488CC',
  belt: '#2A5A2A',
  roller: '#7A7A7A',
  drum: '#7A6A5A',
  copper: '#B87333',
  heater: '#AA4444',
  cable: '#111',
  guard: '#557755'
};

function Label({ text, y = 2.5 }) {
  const hide = useContext(HideLabelsCtx);
  if (hide) return null;
  return (
    <Html position={[0, y, 0]} center>
      <div style={{
        background: 'rgba(0,0,0,0.75)', color: '#FFF', padding: '2px 8px',
        borderRadius: 4, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
        userSelect: 'none', pointerEvents: 'none', letterSpacing: 0.5
      }}>
        {text}
      </div>
    </Html>
  );
}

// ─── HMI SCREEN — removed, merged into MachineStatusOverlay ──
// Kept as no-op for backward compatibility (won't render anything)

export function HMIScreen() {
  return null;
}

// ─── ANIMATED HELPERS ──────────────────────────────────────

// Rotating group — wraps children, rotates on Y axis when active
function RotatingY({ active, speed = 0.02, children }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current && active) ref.current.rotation.y += speed;
  });
  return <group ref={ref}>{children}</group>;
}

// Spinning drum around Z axis (drums are oriented with rotation={[PI/2,0,0]})
// Position must be on parent group OUTSIDE this wrapper
function SpinDrum({ active, speed = 0.03, children }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current && active) ref.current.rotation.z += speed;
  });
  return <group ref={ref}>{children}</group>;
}

// Rotating barrel around its own axis (for extruder screws)
function SpinBarrel({ active, speed = 0.04, children }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current && active) ref.current.rotation.x += speed;
  });
  return <group ref={ref}>{children}</group>;
}

// ─── EXTRUDER ──────────────────────────────────────────────
// size: 45, 75, 120 (screw diameter mm)

export function Extruder({ position = [0, 0, 0], rotation = [0, 0, 0], size = 75, label, screwSpinning = false }) {
  const s = size / 75;
  const bL = 1.8 * s;   // barrel length
  const bR = 0.12 * s;  // barrel radius
  const mL = 0.5 * s;   // motor length
  const mR = 0.22 * s;  // motor radius
  const gS = 0.3 * s;   // gearbox size
  const hH = 0.35 * s;  // hopper height
  const base = 0.7;
  const bands = Math.floor(bL / 0.25);

  return (
    <group position={position} rotation={rotation}>
      {/* Base frame */}
      <mesh position={[0, base / 2, 0]}>
        <boxGeometry args={[bL + mL + gS + 0.4, 0.1, 0.7 * s]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Legs */}
      {[[-1, -1], [-1, 1], [1, -1], [1, 1]].map(([lx, lz], i) => (
        <mesh key={i} position={[lx * (bL / 2 + 0.1), base / 4, lz * 0.3 * s]}>
          <boxGeometry args={[0.06, base / 2, 0.06]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}
      {/* Motor */}
      <mesh position={[-(bL / 2 + gS + mL / 2), base + mR, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[mR, mR, mL, 12]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Gearbox */}
      <mesh position={[-(bL / 2 + gS / 2), base + gS / 2 + 0.02, 0]}>
        <boxGeometry args={[gS, gS, gS * 0.9]} />
        <meshStandardMaterial color={M.body} roughness={0.7} metalness={0.2} />
      </mesh>
      {/* Barrel — rotates when screw spinning (clean-up mode) */}
      <SpinBarrel active={screwSpinning} speed={0.05}>
      <mesh position={[0, base + bR + 0.05, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[bR, bR, bL, 12]} />
        <meshStandardMaterial color={screwSpinning ? '#7A5A4A' : M.barrel} roughness={0.4} metalness={0.5} />
      </mesh>
      {/* Heater bands */}
      {Array.from({ length: bands }, (_, i) => (
        <mesh key={i} position={[-bL / 2 + 0.15 + i * (bL / bands), base + bR + 0.05, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[bR + 0.015, bR + 0.015, 0.06, 12]} />
          <meshStandardMaterial color={M.heater} roughness={0.5} metalness={0.3} />
        </mesh>
      ))}
      </SpinBarrel>
      {/* Hopper */}
      <mesh position={[-bL / 4, base + bR * 2 + hH / 2 + 0.1, 0]}>
        <boxGeometry args={[0.25 * s, hH, 0.25 * s]} />
        <meshStandardMaterial color={M.hopper} roughness={0.8} transparent opacity={0.85} />
      </mesh>
      {/* Crosshead nozzle */}
      <mesh position={[bL / 2 + 0.12, base + bR + 0.05, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[bR * 1.3, bR * 0.6, 0.2, 12]} />
        <meshStandardMaterial color={M.body} roughness={0.5} metalness={0.3} />
      </mesh>
      {label && <Label text={label} y={base + bR * 2 + hH + 0.7} />}
    </group>
  );
}

// ─── DUAL EXTRUDER (L75 + L45 same crosshead) ─────────────

export function DualExtruder({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'L75 + L45', screwSpinning = false }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Main L75 — straight */}
      <Extruder position={[0, 0, 0]} size={75} screwSpinning={screwSpinning} />
      {/* Secondary L45 — angled, feeding into same crosshead from the side */}
      <Extruder position={[1.2, 0, -1.2]} rotation={[0, Math.PI / 4, 0]} size={45} screwSpinning={screwSpinning} />
      {/* Shared crosshead block */}
      <mesh position={[1.3, 0.85, 0]}>
        <boxGeometry args={[0.4, 0.4, 0.5]} />
        <meshStandardMaterial color={M.body} roughness={0.5} metalness={0.4} />
      </mesh>
      <Label text={label} y={2.8} />
    </group>
  );
}

// ─── WATER TROUGH (Jgheab de apa) ──────────────────────────

export function WaterTrough({ position = [0, 0, 0], rotation = [0, 0, 0], length = 5, label }) {
  const legCount = Math.max(2, Math.floor(length / 1.5));
  return (
    <group position={position} rotation={rotation}>
      {/* Trough body */}
      <mesh position={[0, 0.65, 0]}>
        <boxGeometry args={[length, 0.05, 0.45]} />
        <meshStandardMaterial color={M.roller} roughness={0.7} metalness={0.2} />
      </mesh>
      {/* Trough sides */}
      <mesh position={[0, 0.8, 0.2]}>
        <boxGeometry args={[length, 0.25, 0.05]} />
        <meshStandardMaterial color={M.roller} roughness={0.7} metalness={0.2} />
      </mesh>
      <mesh position={[0, 0.8, -0.2]}>
        <boxGeometry args={[length, 0.25, 0.05]} />
        <meshStandardMaterial color={M.roller} roughness={0.7} metalness={0.2} />
      </mesh>
      {/* End caps */}
      <mesh position={[length / 2, 0.8, 0]}>
        <boxGeometry args={[0.05, 0.25, 0.45]} />
        <meshStandardMaterial color={M.roller} roughness={0.7} />
      </mesh>
      <mesh position={[-length / 2, 0.8, 0]}>
        <boxGeometry args={[0.05, 0.25, 0.45]} />
        <meshStandardMaterial color={M.roller} roughness={0.7} />
      </mesh>
      {/* Water */}
      <mesh position={[0, 0.78, 0]}>
        <boxGeometry args={[length - 0.1, 0.15, 0.35]} />
        <meshStandardMaterial color={M.water} roughness={0.2} transparent opacity={0.5} />
      </mesh>
      {/* Legs */}
      {Array.from({ length: legCount }, (_, i) => {
        const x = -length / 2 + 0.3 + i * ((length - 0.6) / (legCount - 1));
        return (
          <React.Fragment key={i}>
            <mesh position={[x, 0.32, 0.18]}>
              <boxGeometry args={[0.05, 0.65, 0.05]} />
              <meshStandardMaterial color={M.frame} />
            </mesh>
            <mesh position={[x, 0.32, -0.18]}>
              <boxGeometry args={[0.05, 0.65, 0.05]} />
              <meshStandardMaterial color={M.frame} />
            </mesh>
          </React.Fragment>
        );
      })}
      {label && <Label text={label} y={1.4} />}
    </group>
  );
}

// ─── CATERPILLAR (Haul-off puller) ─────────────────────────

export function CaterpillarPuller({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Caterpillar' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Frame */}
      <mesh position={[0, 0.6, 0]}>
        <boxGeometry args={[1.2, 1.2, 0.6]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Top belt */}
      <mesh position={[0, 1.05, 0]}>
        <boxGeometry args={[1.0, 0.12, 0.35]} />
        <meshStandardMaterial color={M.belt} roughness={0.8} />
      </mesh>
      {/* Bottom belt */}
      <mesh position={[0, 0.78, 0]}>
        <boxGeometry args={[1.0, 0.12, 0.35]} />
        <meshStandardMaterial color={M.belt} roughness={0.8} />
      </mesh>
      {/* Rollers (ends) */}
      {[-0.45, 0.45].map(x => (
        <React.Fragment key={x}>
          <mesh position={[x, 1.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 0.4, 8]} />
            <meshStandardMaterial color={M.roller} metalness={0.5} />
          </mesh>
          <mesh position={[x, 0.78, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 0.4, 8]} />
            <meshStandardMaterial color={M.roller} metalness={0.5} />
          </mesh>
        </React.Fragment>
      ))}
      {/* Motor */}
      <mesh position={[0, 0.6, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 0.3, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      <Label text={label} y={1.6} />
    </group>
  );
}

// ─── CAPSTAN (Cabestan) ────────────────────────────────────

export function Capstan({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Capstan' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[0.7, 0.6, 0.7]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Drum */}
      <mesh position={[0, 0.9, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.5, 16]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Drum flanges */}
      <mesh position={[0, 0.65, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 0.04, 16]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      <mesh position={[0, 1.15, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 0.04, 16]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      <Label text={label} y={1.6} />
    </group>
  );
}

// ─── DANCER (Vertical tension control — nearly full hall height) ──

export function DancerRoller({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Dancer' }) {
  const H = 4.0;
  const frameW = 1.2;

  return (
    <group position={position} rotation={rotation}>
      {/* Base plate */}
      <mesh position={[0, 0.08, 0]}>
        <boxGeometry args={[frameW + 0.4, 0.16, 1.0]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>

      {/* Two vertical posts (guide rails) */}
      <mesh position={[-frameW / 2, H / 2, 0]}>
        <boxGeometry args={[0.1, H, 0.1]} />
        <meshStandardMaterial color={M.frame} roughness={0.8} />
      </mesh>
      <mesh position={[frameW / 2, H / 2, 0]}>
        <boxGeometry args={[0.1, H, 0.1]} />
        <meshStandardMaterial color={M.frame} roughness={0.8} />
      </mesh>

      {/* Top cross beam */}
      <mesh position={[0, H, 0]}>
        <boxGeometry args={[frameW + 0.1, 0.12, 0.12]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>

      {/* Top fixed pulley */}
      <mesh position={[0, H - 0.2, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 0.35, 12]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>

      {/* Bottom fixed pulley */}
      <mesh position={[0, 0.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 0.35, 12]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>

      {/* Moving carriage (counterweight arm) at mid-height */}
      <mesh position={[0, H * 0.55, 0]}>
        <boxGeometry args={[frameW - 0.15, 0.1, 0.7]} />
        <meshStandardMaterial color={M.safety} roughness={0.6} />
      </mesh>

      {/* Moving pulley on carriage */}
      <mesh position={[0, H * 0.55, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.4, 12]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>

      {/* Guide rail tracks */}
      {[-frameW / 2 + 0.15, frameW / 2 - 0.15].map((x, i) => (
        <mesh key={i} position={[x, H / 2, 0]}>
          <boxGeometry args={[0.04, H - 0.5, 0.04]} />
          <meshStandardMaterial color={M.body} roughness={0.4} metalness={0.5} />
        </mesh>
      ))}

      {/* Counterweight — cylinder hanging on the side */}
      <mesh position={[frameW / 2 + 0.3, H * 0.4, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 1.0, 8]} />
        <meshStandardMaterial color={M.frame} roughness={0.7} metalness={0.4} />
      </mesh>
      <mesh position={[frameW / 2 + 0.3, H * 0.4 + 0.9, 0]}>
        <boxGeometry args={[0.02, H * 0.35, 0.02]} />
        <meshStandardMaterial color={M.cable} />
      </mesh>

      {/* Cable path lines through pulleys */}
      <mesh position={[-0.15, H / 2, 0]}>
        <boxGeometry args={[0.015, H - 0.8, 0.015]} />
        <meshBasicMaterial color="#FF4444" />
      </mesh>
      <mesh position={[0.15, H / 2, 0]}>
        <boxGeometry args={[0.015, H - 0.8, 0.015]} />
        <meshBasicMaterial color="#FF4444" />
      </mesh>

      <Label text={label} y={H + 0.3} />
    </group>
  );
}

// ─── TAKEUP (Cable reel/spool) ─────────────────────────────

export function Takeup({ position = [0, 0, 0], rotation = [0, 0, 0], drumDiameter = 0.8, label = 'Takeup', drumSpinning = false, drumSpeed = 0.03, dotSide = 1 }) {
  const dR = drumDiameter / 2;
  const fR = dR + 0.15;
  const frameH = dR + 0.5;
  return (
    <group position={position} rotation={rotation}>
      {/* Frame — A-frame sides */}
      <mesh position={[0, frameH / 2, 0.35]}>
        <boxGeometry args={[0.08, frameH, 0.08]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      <mesh position={[0, frameH / 2, -0.35]}>
        <boxGeometry args={[0.08, frameH, 0.08]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Cross bar */}
      <mesh position={[0, frameH, 0]}>
        <boxGeometry args={[0.08, 0.08, 0.8]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      {/* Base */}
      <mesh position={[0, 0.05, 0]}>
        <boxGeometry args={[0.8, 0.1, 0.9]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Drum — spins around its own axis when active */}
      <group position={[0, frameH - 0.1, 0]}>
        <SpinDrum active={drumSpinning} speed={drumSpeed}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[dR * 0.35, dR * 0.35, 0.5, 12]} />
            <meshStandardMaterial color={M.drum} roughness={0.7} />
          </mesh>
          <mesh position={[0, 0, 0.25]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[fR, fR, 0.03, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.6} />
          </mesh>
          <mesh position={[0, 0, -0.25]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[fR, fR, 0.03, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.6} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[dR * 0.8, dR * 0.5, 0.4, 16]} />
            <meshStandardMaterial color={M.cable} roughness={0.9} />
          </mesh>
          <mesh position={[fR * 0.7, 0, dotSide * 0.27]}>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color="#FF4444" emissive="#FF2222" emissiveIntensity={0.5} />
          </mesh>
        </SpinDrum>
      </group>
      {/* Motor */}
      <mesh position={[0, 0.3, -0.5]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.25, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      {label && <Label text={label} y={frameH + 0.5} />}
    </group>
  );
}

// ─── SKIP STRANDER 4+1 ────────────────────────────────────

export function SkipStrander({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Skip Strander 4+1' }) {
  const cageR = 0.9;
  const cageL = 2.5;
  return (
    <group position={position} rotation={rotation}>
      {/* Base platform */}
      <mesh position={[0, 0.15, 0]}>
        <boxGeometry args={[cageL + 1.5, 0.3, cageR * 2 + 1]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Cage frame — ring supports */}
      {[-cageL / 2, 0, cageL / 2].map((x, i) => (
        <mesh key={i} position={[x, cageR + 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[cageR, 0.04, 8, 24]} />
          <meshStandardMaterial color={M.body} roughness={0.5} metalness={0.4} />
        </mesh>
      ))}
      {/* Cage horizontal bars */}
      {[0, 1, 2, 3].map(i => {
        const angle = (i * Math.PI) / 2;
        const y = cageR + 0.5 + Math.cos(angle) * cageR;
        const z = Math.sin(angle) * cageR;
        return (
          <mesh key={i} position={[0, y, z]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.03, 0.03, cageL, 6]} />
            <meshStandardMaterial color={M.body} metalness={0.4} />
          </mesh>
        );
      })}
      {/* 4 peripheral bobbins */}
      {[0, 1, 2, 3].map(i => {
        const angle = (i * Math.PI) / 2 + Math.PI / 4;
        const y = cageR + 0.5 + Math.cos(angle) * cageR * 0.65;
        const z = Math.sin(angle) * cageR * 0.65;
        return (
          <mesh key={i} position={[0, y, z]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.15, 0.15, 0.35, 10]} />
            <meshStandardMaterial color={M.copper} roughness={0.6} />
          </mesh>
        );
      })}
      {/* Center bobbin (+1) */}
      <mesh position={[0, cageR + 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.12, 0.12, 0.4, 10]} />
        <meshStandardMaterial color={M.copper} roughness={0.5} />
      </mesh>
      {/* Drive motor side */}
      <mesh position={[-(cageL / 2 + 0.5), 0.6, 0]}>
        <boxGeometry args={[0.7, 0.8, 0.8]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Closing die / capstan side */}
      <mesh position={[(cageL / 2 + 0.4), 0.8, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color={M.body} roughness={0.6} />
      </mesh>
      <Label text={label} y={cageR * 2 + 1.2} />
    </group>
  );
}

// ─── DTB 1250 (Double Twist Buncher) ───────────────────────

export function DTB1250({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'DTB 1250', drumSpinning = false, dotSide = 1 }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base */}
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[2.5, 0.4, 1.8]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Main frame uprights */}
      {[[-0.9, -0.6], [-0.9, 0.6], [0.9, -0.6], [0.9, 0.6]].map(([x, z], i) => (
        <mesh key={i} position={[x, 1, z]}>
          <boxGeometry args={[0.1, 1.6, 0.1]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}
      {/* Bow/flyer — rotating arm */}
      <mesh position={[0, 1.3, 0]} rotation={[0, 0, 0.2]}>
        <torusGeometry args={[0.7, 0.035, 6, 24, Math.PI]} />
        <meshStandardMaterial color={M.safety} roughness={0.5} metalness={0.3} />
      </mesh>
      {/* Large bobbin — spinning */}
      <group position={[0, 1, 0]}>
        <SpinDrum active={drumSpinning} speed={0.025}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.5, 0.5, 0.8, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.6} />
          </mesh>
          <mesh position={[0, 0, 0.42]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.62, 0.62, 0.03, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.5} />
          </mesh>
          <mesh position={[0, 0, -0.42]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.62, 0.62, 0.03, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.5} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.58, 0.52, 0.7, 16]} />
            <meshStandardMaterial color={M.copper} roughness={0.7} />
          </mesh>
          <mesh position={[0.55, 0, dotSide * 0.44]}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#FF4444" emissive="#FF2222" emissiveIntensity={0.5} />
          </mesh>
        </SpinDrum>
      </group>
      {/* Motor */}
      <mesh position={[-1.1, 0.7, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.2, 0.2, 0.5, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      <Label text={label} y={2.3} />
    </group>
  );
}

// ─── OCTABIN (Copper wire cardboard container) ─────────────

export function Octabin({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Octabin' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Support pallet */}
      <mesh position={[0, 0.06, 0]}>
        <boxGeometry args={[1.1, 0.12, 1.1]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Cardboard container — octagonal approximated as cylinder */}
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.45, 0.45, 0.85, 8]} />
        <meshStandardMaterial color="#B8956A" roughness={0.95} />
      </mesh>
      {/* Copper wire inside (visible from top) */}
      <mesh position={[0, 0.95, 0]}>
        <cylinderGeometry args={[0.4, 0.42, 0.08, 8]} />
        <meshStandardMaterial color={M.copper} roughness={0.6} />
      </mesh>
      {/* Wire guide holder above */}
      <mesh position={[0, 1.3, 0]}>
        <boxGeometry args={[0.06, 0.6, 0.06]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      <mesh position={[0, 1.6, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.15, 8]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      {label && <Label text={label} y={1.8} />}
    </group>
  );
}

// ─── TTH HEAD (Tubular Stranding Head) ─────────────────────

export function TTHHead({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'TTH Head' }) {
  const tubeL = 3.0;
  const tubeR = 0.55;
  return (
    <group position={position} rotation={rotation}>
      {/* Base platform */}
      <mesh position={[0, 0.15, 0]}>
        <boxGeometry args={[tubeL + 1.0, 0.3, tubeR * 2 + 0.8]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Support pedestals */}
      {[-tubeL / 2 - 0.2, tubeL / 2 + 0.2].map((x, i) => (
        <mesh key={i} position={[x, 0.55, 0]}>
          <boxGeometry args={[0.3, 0.5, tubeR * 2 + 0.3]} />
          <meshStandardMaterial color={M.frame} roughness={0.8} />
        </mesh>
      ))}
      {/* Main rotating tube */}
      <mesh position={[0, tubeR + 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[tubeR, tubeR, tubeL, 16]} />
        <meshStandardMaterial color={M.body} roughness={0.4} metalness={0.5} transparent opacity={0.85} />
      </mesh>
      {/* Tube end rings */}
      {[-tubeL / 2, tubeL / 2].map((x, i) => (
        <mesh key={i} position={[x, tubeR + 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[tubeR, 0.04, 8, 20]} />
          <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
        </mesh>
      ))}
      {/* Bobbins visible inside tube (6 positions around) */}
      {[0, 1, 2, 3, 4, 5].map(i => {
        const angle = (i * Math.PI * 2) / 6;
        const y = tubeR + 0.5 + Math.cos(angle) * tubeR * 0.5;
        const z = Math.sin(angle) * tubeR * 0.5;
        return (
          <mesh key={i} position={[0, y, z]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.08, 0.08, 0.5, 8]} />
            <meshStandardMaterial color={M.copper} roughness={0.6} />
          </mesh>
        );
      })}
      {/* Drive motor */}
      <mesh position={[-(tubeL / 2 + 0.7), 0.65, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.25, 0.25, 0.5, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Exit guide */}
      <mesh position={[tubeL / 2 + 0.3, tubeR + 0.5, 0]}>
        <boxGeometry args={[0.3, 0.25, 0.25]} />
        <meshStandardMaterial color={M.body} roughness={0.6} />
      </mesh>
      <Label text={label} y={tubeR * 2 + 1.0} />
    </group>
  );
}

// ─── PICK-UP 2250 (Large traversing take-up) ───────────────

export function Pickup2250({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Pick-up 2250', drumSpinning = false, dotSide = 1 }) {
  const dR = 1.1; // 2250mm drum ≈ 1.125m radius
  const fR = dR + 0.2;
  const frameH = dR + 0.7;
  return (
    <group position={position} rotation={rotation}>
      {/* Traversing rail base */}
      <mesh position={[0, 0.06, 0]}>
        <boxGeometry args={[3.5, 0.12, 1.6]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Rails */}
      {[-0.6, 0.6].map((z, i) => (
        <mesh key={i} position={[0, 0.15, z]}>
          <boxGeometry args={[3.5, 0.06, 0.08]} />
          <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
        </mesh>
      ))}
      {/* Main frame on rails (can traverse left-right) */}
      <mesh position={[0.3, 0.35, 0]}>
        <boxGeometry args={[1.8, 0.15, 1.4]} />
        <meshStandardMaterial color={M.frame} roughness={0.8} />
      </mesh>
      {/* Frame uprights */}
      {[[-0.4, -0.55], [-0.4, 0.55], [1.0, -0.55], [1.0, 0.55]].map(([x, z], i) => (
        <mesh key={i} position={[x + 0.3, frameH / 2 + 0.2, z]}>
          <boxGeometry args={[0.1, frameH - 0.3, 0.1]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}
      {/* Top cross beams */}
      <mesh position={[0.3, frameH + 0.1, 0]}>
        <boxGeometry args={[1.5, 0.1, 1.2]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      {/* Large drum — spins around own axis */}
      <group position={[0.3, frameH - 0.2, 0]}>
        <SpinDrum active={drumSpinning} speed={0.02}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[dR * 0.4, dR * 0.4, 0.9, 16]} />
            <meshStandardMaterial color={M.drum} roughness={0.6} />
          </mesh>
          <mesh position={[0, 0, 0.48]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[fR, fR, 0.04, 20]} />
            <meshStandardMaterial color={M.drum} roughness={0.5} />
          </mesh>
          <mesh position={[0, 0, -0.48]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[fR, fR, 0.04, 20]} />
            <meshStandardMaterial color={M.drum} roughness={0.5} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[dR * 0.9, dR * 0.5, 0.8, 16]} />
            <meshStandardMaterial color={M.cable} roughness={0.9} />
          </mesh>
          {/* Marker dot */}
          <mesh position={[fR * 0.7, 0, dotSide * 0.5]}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#FF4444" emissive="#FF2222" emissiveIntensity={0.5} />
          </mesh>
        </SpinDrum>
      </group>
      {/* Traverse motor */}
      <mesh position={[-1.2, 0.3, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.15, 0.15, 0.35, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      <Label text={label} y={frameH + 0.8} />
    </group>
  );
}

// ─── HORIZONTAL DANCER ─────────────────────────────────────

export function HorizontalDancer({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Dancer (H)' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base frame */}
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[1.4, 0.9, 0.8]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Pivot post */}
      <mesh position={[-0.4, 1.2, 0]}>
        <boxGeometry args={[0.15, 0.7, 0.15]} />
        <meshStandardMaterial color={M.frame} roughness={0.8} />
      </mesh>
      {/* Top support */}
      <mesh position={[-0.4, 1.6, 0]}>
        <boxGeometry args={[0.3, 0.12, 0.3]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      {/* Horizontal pivoting arm — longer */}
      <mesh position={[0.6, 1.3, 0]} rotation={[0, 0, -0.06]}>
        <boxGeometry args={[2.2, 0.08, 0.08]} />
        <meshStandardMaterial color={M.safety} roughness={0.6} />
      </mesh>
      {/* Roller at end of arm */}
      <mesh position={[1.65, 1.22, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.14, 0.14, 0.4, 12]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Guide rollers on frame */}
      <mesh position={[-0.4, 1.0, 0.35]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.2, 10]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      <mesh position={[-0.4, 1.0, -0.35]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.2, 10]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      {/* Counterweight at short end */}
      <mesh position={[-0.55, 1.3, 0]}>
        <boxGeometry args={[0.3, 0.2, 0.2]} />
        <meshStandardMaterial color={M.frame} roughness={0.7} metalness={0.3} />
      </mesh>
      <Label text={label} y={2.0} />
    </group>
  );
}

// ─── ACH300 (Automatic Coiling Head) ───────────────────────

export function ACH300({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'ACH 300' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Main rectangular housing */}
      <mesh position={[0, 1.1, 0]}>
        <boxGeometry args={[2.8, 2.0, 1.8]} />
        <meshStandardMaterial color={M.body} roughness={0.7} metalness={0.15} />
      </mesh>
      {/* Top cover / safety guard */}
      <mesh position={[0, 2.15, 0]}>
        <boxGeometry args={[2.9, 0.1, 1.9]} />
        <meshStandardMaterial color={M.safety} roughness={0.6} />
      </mesh>
      {/* Base platform */}
      <mesh position={[0, 0.08, 0]}>
        <boxGeometry args={[3.0, 0.16, 2.0]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>

      {/* Coiling mandrel (rotating arm visible through front) */}
      <mesh position={[0, 1.2, 0.85]}>
        <boxGeometry args={[2.6, 1.6, 0.08]} />
        <meshStandardMaterial color="#4A6A5A" roughness={0.7} transparent opacity={0.4} />
      </mesh>
      {/* Mandrel hub */}
      <mesh position={[0, 1.2, 0.5]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.35, 0.35, 0.3, 16]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Coiling guide arms */}
      {[-0.6, 0.6].map((x, i) => (
        <mesh key={i} position={[x, 1.2, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
          <boxGeometry args={[0.08, 0.5, 0.08]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}

      {/* Finished coil (ring shape on the mandrel) */}
      <mesh position={[0, 1.2, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.4, 0.08, 8, 20]} />
        <meshStandardMaterial color={M.cable} roughness={0.8} />
      </mesh>

      {/* Scissors / shear mechanism */}
      <mesh position={[1.2, 1.5, 0]}>
        <boxGeometry args={[0.3, 0.5, 0.4]} />
        <meshStandardMaterial color={M.heater} roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Shear blades */}
      <mesh position={[1.2, 1.25, 0.1]}>
        <boxGeometry args={[0.2, 0.04, 0.25]} />
        <meshStandardMaterial color={M.roller} roughness={0.2} metalness={0.8} />
      </mesh>

      {/* Ejection chute */}
      <mesh position={[-1.3, 0.6, 0.5]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.5, 0.05, 1.2]} />
        <meshStandardMaterial color={M.roller} roughness={0.5} metalness={0.3} />
      </mesh>
      {/* Chute side rails */}
      {[-0.22, 0.22].map((x, i) => (
        <mesh key={i} position={[-1.3 + x, 0.7, 0.5]} rotation={[0.3, 0, 0]}>
          <boxGeometry args={[0.04, 0.15, 1.2]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}

      {/* Cable entry guide */}
      <mesh position={[1.5, 1.2, 0]}>
        <boxGeometry args={[0.15, 0.3, 0.3]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      <mesh position={[1.5, 1.2, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 0.35, 8]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>

      {/* Drive motor underneath */}
      <mesh position={[0.5, 0.3, -0.7]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.4, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>

      <Label text={label} y={2.6} />
    </group>
  );
}

// ─── OVEN / CUPTOR (packaging/shrink oven for coils) ───────

export function Oven({ position = [0, 0, 0], rotation = [0, 0, 0], width = 4, depth = 2.5, height = 2.2, label = 'Oven' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base */}
      <mesh position={[0, 0.08, 0]}>
        <boxGeometry args={[width + 0.2, 0.16, depth + 0.2]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Main body — insulated box */}
      <mesh position={[0, height / 2 + 0.16, 0]}>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial color="#8A7A6A" roughness={0.85} />
      </mesh>
      {/* Top panel */}
      <mesh position={[0, height + 0.2, 0]}>
        <boxGeometry args={[width + 0.05, 0.08, depth + 0.05]} />
        <meshStandardMaterial color="#6A5A4A" roughness={0.8} />
      </mesh>
      {/* Door / opening (front face) */}
      <mesh position={[0, height / 2 + 0.16, depth / 2 + 0.01]}>
        <boxGeometry args={[width * 0.7, height * 0.8, 0.06]} />
        <meshStandardMaterial color="#5A5A5A" roughness={0.7} metalness={0.2} />
      </mesh>
      {/* Door handle */}
      <mesh position={[width * 0.25, height / 2 + 0.16, depth / 2 + 0.06]}>
        <boxGeometry args={[0.06, 0.3, 0.06]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      {/* Heat indicator strips (red on sides) */}
      {[-1, 1].map((s, i) => (
        <mesh key={i} position={[s * (width / 2 - 0.1), height * 0.7, 0]}>
          <boxGeometry args={[0.08, height * 0.4, depth * 0.6]} />
          <meshStandardMaterial color={M.heater} roughness={0.5} emissive="#331111" />
        </mesh>
      ))}
      {/* Control panel */}
      <mesh position={[width / 2 + 0.15, height * 0.6, 0]}>
        <boxGeometry args={[0.08, 0.5, 0.4]} />
        <meshStandardMaterial color={M.body} roughness={0.6} />
      </mesh>
      <Label text={label} y={height + 0.6} />
    </group>
  );
}

// ─── DHC600 (Semi-auto coiling head) ───────────────────────

export function DHC600({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'DHC 600' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base frame */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[2.0, 0.6, 1.4]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Main body */}
      <mesh position={[0, 1.1, 0]}>
        <boxGeometry args={[1.8, 1.0, 1.2]} />
        <meshStandardMaterial color={M.body} roughness={0.7} metalness={0.15} />
      </mesh>
      {/* Coiling mandrel */}
      <mesh position={[0, 1.2, 0.55]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.3, 0.3, 0.25, 14]} />
        <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Coil on mandrel */}
      <mesh position={[0, 1.2, 0.45]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.35, 0.06, 8, 20]} />
        <meshStandardMaterial color={M.cable} roughness={0.8} />
      </mesh>
      {/* Guide arm */}
      <mesh position={[0.6, 1.5, 0.3]}>
        <boxGeometry args={[0.06, 0.06, 0.6]} />
        <meshStandardMaterial color={M.safety} />
      </mesh>
      <mesh position={[0.6, 1.5, 0.6]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.15, 8]} />
        <meshStandardMaterial color={M.roller} metalness={0.5} />
      </mesh>
      {/* Cable entry guide */}
      <mesh position={[0.9, 1.3, 0]}>
        <boxGeometry args={[0.12, 0.2, 0.2]} />
        <meshStandardMaterial color={M.frame} />
      </mesh>
      {/* Motor */}
      <mesh position={[0, 0.5, -0.6]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 0.3, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Coil ejection tray */}
      <mesh position={[-0.9, 0.5, 0.3]} rotation={[0, 0, 0.15]}>
        <boxGeometry args={[0.4, 0.04, 0.8]} />
        <meshStandardMaterial color={M.roller} roughness={0.5} />
      </mesh>
      <Label text={label} y={2.0} />
    </group>
  );
}

// ─── TAPING HEAD (Aluminium taping machine) ────────────────

export function TapingHead({ position = [0, 0, 0], rotation = [0, 0, 0], label = 'Taping Head' }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Base frame */}
      <mesh position={[0, 0.35, 0]}>
        <boxGeometry args={[1.6, 0.7, 0.9]} />
        <meshStandardMaterial color={M.frame} roughness={0.9} />
      </mesh>
      {/* Main body */}
      <mesh position={[0, 0.9, 0]}>
        <boxGeometry args={[1.2, 0.5, 0.7]} />
        <meshStandardMaterial color={M.body} roughness={0.7} metalness={0.2} />
      </mesh>
      {/* Rotating taping disc */}
      <mesh position={[0, 1.0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry args={[0.28, 0.03, 8, 20]} />
        <meshStandardMaterial color="#AAAAAA" roughness={0.3} metalness={0.7} />
      </mesh>
      {/* Aluminium tape spool */}
      <mesh position={[0, 1.0, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 0.08, 12]} />
        <meshStandardMaterial color="#C0C0C0" roughness={0.3} metalness={0.8} />
      </mesh>
      <mesh position={[0, 1.0, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.1, 12]} />
        <meshStandardMaterial color="#D8D8D8" roughness={0.2} metalness={0.6} />
      </mesh>
      {/* Cable entry/exit guides */}
      {[-0.7, 0.7].map((x, i) => (
        <mesh key={i} position={[x, 0.9, 0]}>
          <boxGeometry args={[0.12, 0.2, 0.2]} />
          <meshStandardMaterial color={M.frame} />
        </mesh>
      ))}
      {/* Entry/exit rollers */}
      {[-0.7, 0.7].map((x, i) => (
        <mesh key={`r-${i}`} position={[x, 0.9, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.04, 0.04, 0.25, 8]} />
          <meshStandardMaterial color={M.roller} metalness={0.5} />
        </mesh>
      ))}
      {/* Motor */}
      <mesh position={[0.5, 0.4, -0.5]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.25, 10]} />
        <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
      </mesh>
      <Label text={label} y={1.6} />
    </group>
  );
}

// ─── CABLE PATH LINE ───────────────────────────────────────

export function CablePath({ points, color = '#FF4444' }) {
  if (!points || points.length < 2) return null;
  return (
    <group>
      {points.map((p, i) => {
        if (i === points.length - 1) return null;
        const next = points[i + 1];
        const dx = next[0] - p[0];
        const dy = next[1] - p[1];
        const dz = next[2] - p[2];
        const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const mx = (p[0] + next[0]) / 2;
        const my = (p[1] + next[1]) / 2;
        const mz = (p[2] + next[2]) / 2;
        return (
          <mesh key={i} position={[mx, my, mz]} rotation={[0, -Math.atan2(dz, dx), 0]}>
            <cylinderGeometry args={[0.015, 0.015, len, 4]} />
            <meshBasicMaterial color={color} />
          </mesh>
        );
      })}
    </group>
  );
}

// ─── HALL 1 MACHINES LAYOUT ────────────────────────────────
// Hall 1: X=-21 to -4, Z=0 to 82.7 (17m wide, 82.7m long)
// Right wall at X=-4, door at Z=46.5
//
// LINE 1 (L75+L45): Right side near wall, starts after door
// Cable flow: Z increasing →
// X position: ~-6.5 (near right wall X=-4)

export function Hall1Machines({ machineData = {}, onOpenPanel, hideLabels = false }) {
  const RX = -6.5;  // right side near wall (X=-4)
  const LX = -25.5; // left side near wall (X=-28)

  const l75 = machineData.l75;
  const l120 = machineData.l120;
  const dtb = machineData.dtb1250;
  const skip = machineData.skip2250;

  // Animation flags
  const l75Running = l75?.isRunning;
  const l75Drum = l75Running && (l75?.mode === 'PRODUCTION' || l75?.mode === 'UNWINDING');
  const l75TU1 = l75Running && !!l75?.takeup1Active;
  const l75TU2 = l75Running && !!l75?.takeup2Active;
  const l75Screw = l75Running && l75?.mode === 'CLEAN UP';
  const l120Running = l120?.isRunning;
  const l120Drum = l120Running && (l120?.mode === 'PRODUCTION' || l120?.mode === 'UNWINDING');
  const l120Screw = l120Running && l120?.mode === 'CLEAN UP';
  const dtbDrum = dtb?.isRunning;
  const skipDrum = skip?.isRunning;

  return (
    <HideLabelsCtx.Provider value={hideLabels}>
    <group>
      {/* ═══════════════════════════════════════════════════════
          Both lines on RIGHT SIDE (X=-6.5), door (Z=46.5) between
          L75+L45: LEFT of door → toward gate (Z decreasing)
          Skip/TTH: RIGHT of door → toward back (Z increasing)
          ═══════════════════════════════════════════════════════ */}

      {/* ── Real-time status overlays ──────────────────────── */}
      {l75 && !hideLabels && (
        <>
          <MachineStatusOverlay position={[RX + 2.5, 3.5, 39]} data={l75} machineId="l75" onOpen={onOpenPanel} />
          <StatusLight position={[RX, 2.5, 39]} status={l75.status} />
          <L75ModeIndicator position={[RX, 3, 33]} data={l75} machineId="l75" onOpen={onOpenPanel} />
        </>
      )}
      {skip && !hideLabels && (
        <>
          <MachineStatusOverlay position={[RX + 2.5, 3.5, 60]} data={skip} machineId="skip2250" onOpen={onOpenPanel} />
          <StatusLight position={[RX, 2.5, 60]} status={skip.status} />
        </>
      )}
      {dtb && !hideLabels && (
        <>
          <MachineStatusOverlay position={[LX + 2.5, 3.5, 60]} data={dtb} machineId="dtb1250" onOpen={onOpenPanel} />
          <StatusLight position={[LX, 2.5, 60]} status={dtb.status} />
          <PayoffIndicators position={[LX, 2.5, 70]} payoffs={dtb.payoffs} />
        </>
      )}
      {l120 && !hideLabels && (
        <>
          <MachineStatusOverlay position={[LX + 2.5, 3.5, 25]} data={l120} machineId="l120" onOpen={onOpenPanel} />
          <StatusLight position={[LX, 2.5, 21]} status={l120.status} />
        </>
      )}

      {/* ── HMI Screens ───────────────────────────────────── */}
      {/* L75: between extruder (Z=39) and cooling trough (Z=33) */}
      <HMIScreen position={[RX + 2, 0, 36]} rotation={[0, Math.PI, 0]} label="L75" data={l75} machineId="l75" onOpen={onOpenPanel} />
      <HMIScreen position={[RX + 3, 0, 55]} rotation={[0, -Math.PI / 2, 0]} label="SKIP 2250" data={skip} machineId="skip2250" onOpen={onOpenPanel} />
      <HMIScreen position={[LX + 3, 0, 63]} rotation={[0, Math.PI / 2, 0]} label="DTB 1250" data={dtb} machineId="dtb1250" onOpen={onOpenPanel} />
      <HMIScreen position={[LX + 2, 0, 28]} rotation={[0, Math.PI / 2, 0]} label="L120" data={l120} machineId="l120" onOpen={onOpenPanel} />

      {/* ═══ LINE 1: L75+L45 Isolation ══════════════════════ */}
      {/* LEFT of door → toward gate (Z decreasing)            */}

      {/* Pay-off at Z=43, octabin between it and extruder */}
      <Takeup
        position={[RX, 0, 43]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={0.8}
        label="Pay-off 1250"
        drumSpinning={l75Drum}
      />

      <Octabin position={[RX, 0, 41]} label="Octabin (L75)" />

      <DualExtruder
        position={[RX, 0, 37]}
        rotation={[0, Math.PI, 0]}
        label="L75 + L45"
        screwSpinning={l75Screw}
      />

      <WaterTrough
        position={[RX, 0, 31]}
        rotation={[0, Math.PI / 2, 0]}
        length={6}
        label="Cooling Trough"
      />

      <Capstan
        position={[RX, 0, 26]}
        label="Capstan"
      />

      <CaterpillarPuller
        position={[RX, 0, 23]}
        rotation={[0, -Math.PI / 2, 0]}
        label="Caterpillar"
      />

      <DancerRoller
        position={[RX, 0, 19]}
        label="Dancer"
      />

      <Takeup
        position={[RX, 0, 15]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={1.0}
        label="Take-up 1"
        drumSpinning={l75TU1}
      />

      <Takeup
        position={[RX, 0, 11]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={1.0}
        label="Take-up 2"
        drumSpinning={l75TU2}
      />

      <CablePath
        points={[
          [RX, 0.9, 43], [RX, 0.9, 41], [RX, 0.9, 37],
          [RX, 0.9, 34], [RX, 0.9, 31], [RX, 0.9, 26],
          [RX, 0.9, 23], [RX, 0.9, 19], [RX, 0.9, 15],
          [RX, 0.9, 11]
        ]}
        color="#FF4444"
      />

      {/* ═══ LINE 2: Skip / TTH Line ═══════════════════════ */}
      {/* RIGHT of door → toward back wall (Z increasing)     */}
      {/* Pick-up 2250 → Capstan → TTH Head → 5x Pick-up 1250 */}

      <Pickup2250
        position={[RX, 0, 50]}
        rotation={[0, Math.PI / 2, 0]}
        label="Take-up 2250"
        drumSpinning={skipDrum}
        dotSide={-1}
      />

      <Capstan
        position={[RX, 0, 55]}
        label="Capstan"
      />

      <TTHHead
        position={[RX, 0, 60]}
        rotation={[0, Math.PI / 2, 0]}
        label="TTH Head"
      />

      {Array.from({ length: 5 }, (_, i) => (
        <Takeup
          key={`tth-pu-${i}`}
          position={[RX + (i % 2 === 0 ? 0 : -1.5), 0, 67 + i * 3]}
          rotation={[0, Math.PI / 2, 0]}
          drumDiameter={1.0}
          label={`Pay-off 1250 #${i + 1}`}
        />
      ))}

      <CablePath
        points={[
          [RX, 0.9, 50], [RX, 0.9, 55], [RX, 0.9, 60],
          [RX, 0.9, 67], [RX, 0.9, 70], [RX, 0.9, 73],
          [RX, 0.9, 76], [RX, 0.9, 79]
        ]}
        color="#44AAFF"
      />

      {/* ═══ LINE 3: DTB 1250 + 7 Pay-offs ═════════════════ */}
      {/* LEFT wall, moved toward back wall                    */}

      <DTB1250
        position={[LX, 0, 60]}
        rotation={[0, Math.PI / 2, 0]}
        label="DTB 1250"
        drumSpinning={dtbDrum}
        dotSide={1}
      />

      {Array.from({ length: 7 }, (_, i) => (
        <Takeup
          key={`dtb-po-${i}`}
          position={[LX + (i % 2 === 0 ? 0 : 1.2), 0, 64 + i * 2.5]}
          rotation={[0, Math.PI / 2, 0]}
          drumDiameter={0.8}
          label={`Pay-off 1000 #${i + 1}`}
          drumSpinning={dtbDrum && dtb?.payoffs?.[i]?.active}
        />
      ))}

      <CablePath
        points={[
          [LX, 0.9, 60], [LX, 0.9, 64], [LX, 0.9, 66.5],
          [LX, 0.9, 69], [LX, 0.9, 71.5], [LX, 0.9, 74],
          [LX, 0.9, 76.5], [LX, 0.9, 79]
        ]}
        color="#44CC44"
      />

      {/* ═══ 7 OCTABINS (copper) — back wall, starting from DTB side */}
      {Array.from({ length: 7 }, (_, i) => (
        <Octabin
          key={`oct-${i}`}
          position={[LX + 2 + i * 2, 0, 81]}
          label={`Octabin #${i + 1}`}
        />
      ))}

      {/* ═══ LINE 4: L120 Tandem ════════════════════════════ */}
      {/* LEFT wall, parallel to L75 line, from Z=13 toward Z=40 */}
      {/* PAY-OFF 2000 → Caterpillar → L120 #1 → L120 #2 →      */}
      {/* Cooling Trough → Caterpillar → Dancer → TAKE-UP 2000   */}

      <Takeup
        position={[LX, 0, 13]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={1.5}
        label="Pay-off 2000"
        drumSpinning={l120Drum}
      />

      <CaterpillarPuller
        position={[LX, 0, 17]}
        rotation={[0, Math.PI / 2, 0]}
        label="Caterpillar"
      />

      <Extruder
        position={[LX, 0, 21]}
        rotation={[0, 0, 0]}
        size={120}
        label="L120 #1"
        screwSpinning={l120Screw}
      />

      <Extruder
        position={[LX, 0, 25]}
        rotation={[0, 0, 0]}
        size={120}
        label="L120 #2"
        screwSpinning={l120Screw}
      />

      <WaterTrough
        position={[LX, 0, 31]}
        rotation={[0, Math.PI / 2, 0]}
        length={6}
        label="Cooling Trough"
      />

      <CaterpillarPuller
        position={[LX, 0, 36]}
        rotation={[0, Math.PI / 2, 0]}
        label="Caterpillar"
      />

      <DancerRoller
        position={[LX, 0, 39]}
        label="Dancer"
      />

      <Takeup
        position={[LX, 0, 43]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={1.5}
        label="Take-up 2000"
        drumSpinning={l120Drum}
      />

      <CablePath
        points={[
          [LX, 0.9, 13], [LX, 0.9, 17], [LX, 0.9, 21],
          [LX, 0.9, 25], [LX, 0.9, 28], [LX, 0.9, 31],
          [LX, 0.9, 36], [LX, 0.9, 39], [LX, 0.9, 43]
        ]}
        color="#FF8844"
      />
    </group>
    </HideLabelsCtx.Provider>
  );
}

// ─── HALL 3 MACHINES LAYOUT ────────────────────────────────
// Hall 3: X=11 to 28, Z=0 to 41 (17m wide, 41m long)
// Door on left wall (X=11) at Z=17
// ACH300 line on the long wall near door (X≈13), starts at
// middle of hall (Z≈20) and goes toward right (Z decreasing)

export function Hall3Machines({ machineData = {}, onOpenPanel, hideLabels = false }) {
  const WX = 33;   // near right wall (X=35)
  const CX = 19.5; // center of hall

  const ach = machineData.ach300;
  const dhc = machineData.dhc600;
  const achDrum = ach?.isRunning;
  const dhcDrum = dhc?.isRunning;

  return (
    <HideLabelsCtx.Provider value={hideLabels}>
    <group>
      {/* ── Real-time status overlays ──────────────────────── */}
      {ach && !hideLabels && (
        <>
          <MachineStatusOverlay position={[WX - 3, 3.5, 6]} data={ach} machineId="ach300" onOpen={onOpenPanel} />
          <StatusLight position={[WX, 2.5, 6]} status={ach.status} />
        </>
      )}
      {dhc && !hideLabels && (
        <>
          <MachineStatusOverlay position={[WX - 3, 3.5, 33]} data={dhc} machineId="dhc600" onOpen={onOpenPanel} />
          <StatusLight position={[WX, 2.5, 33]} status={dhc.status} />
        </>
      )}

      {/* HMI Screens */}
      <HMIScreen position={[WX - 2, 0, 8]} rotation={[0, -Math.PI / 2, 0]} label="ACH 300" data={ach} machineId="ach300" onOpen={onOpenPanel} />
      <HMIScreen position={[WX - 2, 0, 33]} rotation={[0, -Math.PI / 2, 0]} label="DHC 600" data={dhc} machineId="dhc600" onOpen={onOpenPanel} />

      {/* ═══ OVEN #1 (small) — near door wall, middle area ═══ */}
      <Oven
        position={[16, 0, 2]}
        rotation={[0, 0, 0]}
        width={4}
        depth={2.0}
        height={2.0}
        label="Oven (Packaging)"
      />

      {/* ═══ ACH300 Coiling Line — pushed toward wall ═══════ */}
      {/* Right wall, starts at Z≈14, flows toward Z=0          */}

      <Takeup
        position={[WX, 0, 14]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={1.0}
        label="Pay-off 1250"
        drumSpinning={achDrum}
        dotSide={-1}
      />

      <HorizontalDancer
        position={[WX, 0, 10]}
        rotation={[0, -Math.PI / 2, 0]}
        label="Dancer (Horizontal)"
      />

      <ACH300
        position={[WX, 0, 6]}
        rotation={[0, -Math.PI / 2, 0]}
        label="ACH 300"
      />

      <CablePath
        points={[[WX, 0.9, 14], [WX, 0.9, 10], [WX, 0.9, 6]]}
        color="#CC44CC"
      />

      {/* ═══ DHC600 Line — more space from ACH300 ═══════════ */}
      {/* Pay-off 1600 → Vertical Dancer → DHC600 dual head    */}

      <Takeup
        position={[WX, 0, 24]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={1.3}
        label="Pay-off 1600"
        drumSpinning={dhcDrum}
        dotSide={-1}
      />

      <DancerRoller
        position={[WX, 0, 28]}
        label="Dancer (Vertical)"
      />

      {/* DHC600 — dual head, one solid machine */}
      <group position={[WX, 0, 33]} rotation={[0, -Math.PI / 2, 0]}>
        {/* Single unified base */}
        <mesh position={[0, 0.08, 0]}>
          <boxGeometry args={[5.0, 0.16, 2.2]} />
          <meshStandardMaterial color={M.frame} roughness={0.9} />
        </mesh>
        {/* Unified body — one continuous frame */}
        <mesh position={[0, 0.55, 0]}>
          <boxGeometry args={[4.8, 0.7, 1.8]} />
          <meshStandardMaterial color={M.frame} roughness={0.85} />
        </mesh>
        {/* Shared upper housing */}
        <mesh position={[0, 1.2, 0]}>
          <boxGeometry args={[4.6, 0.8, 1.4]} />
          <meshStandardMaterial color={M.body} roughness={0.7} metalness={0.15} />
        </mesh>
        {/* Top cover */}
        <mesh position={[0, 1.65, 0]}>
          <boxGeometry args={[4.7, 0.06, 1.5]} />
          <meshStandardMaterial color={M.safety} roughness={0.6} />
        </mesh>
        {/* Mandrel #1 */}
        <mesh position={[-1.3, 1.2, 0.65]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 14]} />
          <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
        </mesh>
        {/* Mandrel #2 */}
        <mesh position={[1.3, 1.2, 0.65]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 14]} />
          <meshStandardMaterial color={M.roller} roughness={0.3} metalness={0.6} />
        </mesh>
        {/* Coils on mandrels — facing outward (away from wall) */}
        <mesh position={[-1.3, 1.2, 0.55]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.35, 0.06, 8, 20]} />
          <meshStandardMaterial color={M.cable} roughness={0.8} />
        </mesh>
        <mesh position={[1.3, 1.2, 0.55]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.35, 0.06, 8, 20]} />
          <meshStandardMaterial color={M.cable} roughness={0.8} />
        </mesh>
        {/* Finished coils ejected — small coils on the floor side */}
        {[-1.3, 1.3].map((x, i) => (
          <mesh key={i} position={[x, 0.3, 0.9]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.22, 0.04, 8, 16]} />
            <meshStandardMaterial color={M.cable} roughness={0.9} />
          </mesh>
        ))}
        {/* Guide arms */}
        {[-1.3, 1.3].map((x, i) => (
          <mesh key={`ga-${i}`} position={[x, 1.5, 0.4]}>
            <boxGeometry args={[0.06, 0.06, 0.5]} />
            <meshStandardMaterial color={M.safety} />
          </mesh>
        ))}
        {/* Motor (shared) */}
        <mesh position={[0, 0.5, -0.8]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.2, 0.2, 0.4, 10]} />
          <meshStandardMaterial color={M.motor} roughness={0.6} metalness={0.3} />
        </mesh>
        <Label text="DHC 600 (Dual)" y={2.2} />
      </group>

      <CablePath
        points={[[WX, 0.9, 24], [WX, 0.9, 28], [WX, 0.9, 33]]}
        color="#44CCCC"
      />

      {/* ═══ OVEN #2 (large, rotated 90°) ═══════════════════ */}
      <Oven
        position={[CX, 0, 39]}
        rotation={[0, Math.PI, 0]}
        width={7}
        depth={2.5}
        height={2.5}
        label="Oven (Large)"
      />
    </group>
    </HideLabelsCtx.Provider>
  );
}

// ─── HALL 4 MACHINES LAYOUT ────────────────────────────────
// Hall 4: X=11 to 28, Z=47.3 to 82.3 (17m wide, 35m long)
// Door from entrance at Z=47.3
// L70 isolation line — like L75 but single extruder, no caterpillar
// Pick-ups near door, take-up at back wall, full length

export function Hall4Machines({ machineData = {}, onOpenPanel, hideLabels = false }) {
  const WX = 33;   // near right wall (X=35)
  const LX = 13.5; // near left wall (X=11)

  const l70 = machineData.l70;
  const ath = machineData.ath600;
  const l70Running = l70?.isRunning;
  // L70 is RPi — may not have mode, spin drum if running
  const l70Drum = l70Running;
  const l70Screw = false; // RPi machines don't report clean-up mode
  const athDrum = ath?.isRunning; // RPi — spin if running

  return (
    <HideLabelsCtx.Provider value={hideLabels}>
    <group>
      {/* ── Real-time status overlays ──────────────────────── */}
      {l70 && !hideLabels && (
        <>
          <MachineStatusOverlay position={[WX - 3, 3.5, 73]} data={l70} machineId="l70" onOpen={onOpenPanel} />
          <StatusLight position={[WX, 2.5, 73]} status={l70.status} />
        </>
      )}
      {ath && !hideLabels && (
        <>
          <MachineStatusOverlay position={[LX + 3, 3.5, 66]} data={ath} machineId="ath600" onOpen={onOpenPanel} />
          <StatusLight position={[LX, 2.5, 66]} status={ath.status} />
        </>
      )}

      {/* HMI Screens */}
      <HMIScreen position={[WX - 2, 0, 70]} rotation={[0, -Math.PI / 2, 0]} label="L70" data={l70} machineId="l70" onOpen={onOpenPanel} />
      <HMIScreen position={[LX + 3, 0, 63]} rotation={[0, Math.PI / 2, 0]} label="ATH 600" data={ath} machineId="ath600" onOpen={onOpenPanel} />

      {/* ═══ L70 Isolation Line ═════════════════════════════ */}
      {/* From door (Z=47.3) toward back wall (Z=82.3)         */}
      {/* Pick-ups → Dancer → Capstan → Trough → L70 → Take-up */}

      <Takeup
        position={[WX, 0, 50]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={1.0}
        label="Take-up 1000"
      />

      <Takeup
        position={[WX, 0, 54]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={1.0}
        label="Take-up 1000"
        drumSpinning={l70Drum}
        drumSpeed={-0.03}
        dotSide={-1}
      />

      <DancerRoller
        position={[WX, 0, 58]}
        label="Dancer (Vertical)"
      />

      <Capstan
        position={[WX, 0, 62]}
        label="Capstan"
      />

      <WaterTrough
        position={[WX, 0, 67]}
        rotation={[0, Math.PI / 2, 0]}
        length={6}
        label="Cooling Trough"
      />

      <Extruder
        position={[WX, 0, 73]}
        rotation={[0, Math.PI, 0]}
        size={75}
        label="L70"
        screwSpinning={l70Screw}
      />

      {/* Octabin for L70 — between extruder and pay-off, on the line */}
      <Octabin position={[WX, 0, 76]} label="Octabin (L70)" />

      <Takeup
        position={[WX, 0, 79]}
        rotation={[0, Math.PI / 2, 0]}
        drumDiameter={0.8}
        label="Pay-off"
        drumSpinning={l70Drum}
      />

      <CablePath
        points={[
          [WX, 0.9, 50], [WX, 0.9, 54], [WX, 0.9, 58],
          [WX, 0.9, 62], [WX, 0.9, 67], [WX, 0.9, 73],
          [WX, 0.9, 76], [WX, 0.9, 79]
        ]}
        color="#FF4444"
      />

      {/* ═══ Aluminium Taping Line ══════════════════════════ */}
      {/* Left wall (X≈13), parallel to cooling trough area    */}
      {/* Direction: toward entrance (Z decreasing)            */}
      {/* Pay-off 1600 → Taping Head → Caterpillar → Take-up 1600 */}

      <Takeup
        position={[LX, 0, 70]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={1.3}
        label="Pay-off 1600"
        drumSpinning={athDrum}
      />

      <TapingHead
        position={[LX, 0, 66]}
        rotation={[0, -Math.PI / 2, 0]}
        label="Al Taping Head"
      />

      <CaterpillarPuller
        position={[LX, 0, 62]}
        rotation={[0, -Math.PI / 2, 0]}
        label="Caterpillar"
      />

      <Takeup
        position={[LX, 0, 58]}
        rotation={[0, -Math.PI / 2, 0]}
        drumDiameter={1.3}
        label="Take-up 1600"
        drumSpinning={athDrum}
      />

      <CablePath
        points={[
          [LX, 0.9, 70], [LX, 0.9, 66],
          [LX, 0.9, 62], [LX, 0.9, 58]
        ]}
        color="#44AAFF"
      />
    </group>
    </HideLabelsCtx.Provider>
  );
}
